<template>
  <div class="lv-state-wysiwyg">
    <jodit-vue
      ref="editor"
      v-model="text"
      :buttons="buttons"
      :config="config"
      class="lv-state-wysiwyg-editor"
    />
    <LvDialogImgPlatformStock
      ref="LvDialogImgPlatformStock"
      :isChooseImage="embed.isChooseImage"
      @input="$_onChooseFile"
    />
  </div>
</template>
<script>
import { JoditVue } from 'jodit-vue';
import 'jodit/build/jodit.min.css';

export default {
  components: { JoditVue },
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isModifying: false,
      text: '',
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'eraser',
        'ul',
        'ol',
        'font',
        'fontsize',
        'paragraph',
        'lineHeight',
        'superscript',
        'subscript',
        'spellcheck',
        'cut',
        'copy',
        'paste',
        'selectall',
        'copyformat',
        'hr',
        'table',
        'link',
        'symbols',
        'indent',
        'outdent',
        'left',
        'undo',
        'redo',
        'find',
        'fullsize',
        'preview',
        'source',
        'customImage',
        'brush',
      ],
      config: {
        cleanHTML: {
          removeEmptyElements: true,
          fillEmptyParagraph: false,
          replaceNBSP: true,
          removeOnError: true,
        },
        processPasteHTML: true,
        askBeforePasteHTML: false,
        scrollToPastedContent: true,
        events: {
          processPaste: ($event, html) => {
            const emptyTagPattern = /<(\w+)[^>]*>\s*<\/\1>/g;
            const removeStylePattern = /<style\b[^>]*>[\s\S]*?<\/style>/g;
            // 先進行其他樣式處理
            let processedHtml = html
              .replace(
                /<p[^>]*>/g,
                '<p style="line-height: 1.78;font-size: 1.125rem;letter-spacing: 0.8px;">',
              )
              .replace(
                /<span[^>]*>/g,
                '<span  style="line-height: 1.78;font-size: 1.125rem;letter-spacing: 0.8px;">',
              )
              .replace(/<ul[^>]*>/g, match => {
                // 保留原本的 style，加上 line-height
                if (match.includes('style=')) {
                  return match.replace(/style="([^"]*)"/, 'style="$1;line-height: 1.78;"');
                } else {
                  return match.replace('>', ' style="line-height: 1.78;">');
                }
              })
              .replace(/<td[^>]*>/g, match => {
                // 保留原本的 style，加上 line-height
                if (match.includes('style=')) {
                  return match.replace(/style="([^"]*)"/, 'style="line-height: 1.78; width: 30%"');
                } else {
                  return match.replace('>', ' style="line-height: 1.78; width: 30%;">');
                }
              })
              .replace(/<table[^>]*>/g, match => {
                if (match.includes('style=')) {
                  return match.replace(
                    /style="([^"]*)"/,
                    'style="table-layout: fixed; width: 100%;"',
                  );
                } else {
                  return match.replace('>', ' style="table-layout: fixed; width: 100%;">');
                }
              })
              .replace(/<div[^>]*>/g, '<div>')
              .replace(/<ol[^>]*>/g, '<div>')
              .replace(/<li[^>]*>/g, '<div>')
              .replace(/<title[^>]*>/g, '<p>')
              .replace(/<\/title>/g, '</p>')
              .replace(removeStylePattern, '')
              .replace(emptyTagPattern, '')
              .replace(/\n/g, '');

            // 最後處理表格 td 數量標準化
            processedHtml = this.$_normalizeTableTdCount(processedHtml);

            return processedHtml;
          },
        },
        createAttributes: {
          table: {
            style: 'border:1px solid black;border-collapse:collapse;width: 100%;',
          },
          tr: { style: 'border: 1px solid black;' },
          td: { style: 'border: 1px solid black;padding: 8px;' },
          p: { style: 'line-height: 1.78;font-size: 1.125rem;letter-spacing: 0.8px;' },
        },
        controls: {
          fontsize: {
            list: [
              '8',
              '9',
              '10',
              '11',
              '12',
              '14',
              '16',
              '18',
              '20',
              '22',
              '24',
              '26',
              '28',
              '30',
              '32',
              '36',
              '48',
              '60',
              '72',
              '96',
            ],
          },
          customImage: {
            icon: 'image',
            tooltip: '插入圖片',
            exec: () => {
              this.embed.isChooseImage = true;
              this.$refs.LvDialogImgPlatformStock.open();
            },
          },
        },
      },
      embed: {
        isChooseImage: false,
      },
    };
  },
  watch: {
    value(newVal) {
      if (newVal !== this.text) this.text = newVal;
    },
    text(newVal) {
      if (this.isModifying) return;
      if (!newVal || !newVal.trim()) {
        this.$emit('input', newVal);
        return;
      }
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${newVal}</div>`, 'text/html'); // 包裝成 <div> 以處理片段 HTML
      if (!doc.body.firstChild) {
        this.$emit('input', newVal);
        return;
      }
      this.isModifying = true;
      const pTags = doc.querySelectorAll('p');
      pTags.forEach((p) => {
        if (p.querySelector('img')) {
          // 將 img 標籤從 p 標籤中移出來
          const imgTags = p.querySelectorAll('img');
          imgTags.forEach((img) => {
            p.insertAdjacentElement('afterend', img);
          });
        } else {
          // 為純文字的 p 標籤添加樣式
          if (p.hasAttribute('style')) {
            const style = p.style;
            if (!style.lineHeight) {
              style.lineHeight = '1.78';
            }
          } else {
            p.setAttribute('style', 'line-height: 1.78;');
          }
        }
        // 如果 p 標籤為空或只包含 br 標籤才刪除
        if (p.children.length === 0 && !p.textContent.trim()) {
          p.remove();
        }
      });
      this.text = doc.body.firstChild.innerHTML;
      this.$emit('input', doc.body.firstChild.innerHTML || '');
      this.isModifying = false;
    },
  },
  mounted() {
    this.text = this.value || '';
  },
  methods: {
    $_normalizeTableTdCount(html) {
      // 使用 DOMParser 解析 HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // 找到所有表格
      const tables = doc.querySelectorAll('table');

      tables.forEach((table) => {
        // 找到表格內所有的 tr，包括在 tbody 內的
        const rows = table.querySelectorAll('tr');
        if (rows.length === 0) return;

        // 找出最多 td 的數量
        let maxTdCount = 0;
        rows.forEach((row) => {
          const tds = row.querySelectorAll('td');
          maxTdCount = Math.max(maxTdCount, tds.length);
        });

        // 為每個 row 補齊 td
        rows.forEach((row) => {
          const tds = row.querySelectorAll('td');
          const currentTdCount = tds.length;

          if (currentTdCount < maxTdCount) {
            // 需要補齊的 td 數量
            const needToAdd = maxTdCount - currentTdCount;

            // 取得最後一個 td 的樣式作為參考
            const lastTd = tds[tds.length - 1];
            let referenceStyle = '';
            if (lastTd && lastTd.hasAttribute('style')) {
              referenceStyle = lastTd.getAttribute('style');
            } else {
              referenceStyle = 'border: 1px solid black;padding: 8px;';
            }

            // 補齊空白 td
            for (let i = 0; i < needToAdd; i++) {
              const emptyTd = document.createElement('td');
              emptyTd.setAttribute('style', referenceStyle);
              emptyTd.innerHTML = '&nbsp;';
              row.appendChild(emptyTd);
            }
          }
        });
      });

      return doc.body.innerHTML;
    },
    $_onChooseFile($event) {
      setTimeout(() => {
        if (!this.$refs.editor) return;
        const editor = this.$refs.editor.editor;
        if (this.embed.isChooseImage) {
          for (let i = 0; i < $event.length; i++) {
            const url = $event[i].url || $event[i].source_url;
            const img = document.createElement('img');
            img.src = url;
            img.alt = 'image';
            img.style.cssText = 'max-width:600px; height:auto; width:100%;';
            editor.selection.insertNode(img);
          }
        }
      }, 1);
    },
  },
};
</script>

<style lang="scss">
@import '../assets/scss/lv-state-wysiwyg.scss';
</style>
